import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GoogleMapService } from '../../../../../shared/modules/google-maps/google-map.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { IApiUser } from 'src/app/shared/modules/graphql/types/types';

export type DistanceSelectionMetric = {
  id: string;
  distance?: number;
  duration?: number;
};

export type DistanceSelectionPayload = {
  userIds: string[];
  metrics: DistanceSelectionMetric[];
};

type DistanceFilterUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  itemFullName?: string;
  HomeAddress?: {
    city?: string;
    state?: string;
    latitude?: number;
    longitude?: number;
  };
  distance?: number;
  duration?: number;
  _searchText?: string;
  _originalIndex?: number;
};

@Component({
  selector: 'app-calendar-user-distance-filter',
  templateUrl: './calendar-user-distance-filter.component.html',
  styleUrls: ['./calendar-user-distance-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarUserDistanceFilterComponent implements OnInit {
  @ViewChild('multiSelect') multiSelect!: MultiSelectComponent;
  @Input() selectedId: any[] = [];
  @Output() selectionChanged = new EventEmitter<any[]>();
  @Output() selectionWithMetricsChanged = new EventEmitter<DistanceSelectionPayload>();

  public allUsers: DistanceFilterUser[] = [];
  public filteredUsers: DistanceFilterUser[] = [];
  public proximityAddress: any = null;
  public loading: boolean = false;
  public searchString: string = '';
  public readonly Infinity = Infinity;
  private selectedIdSet = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private _mapService: GoogleMapService,
  ) { }

  ngOnInit(): void {
    const users = this.route.snapshot.data['users'] || [];
    this.allUsers = users?.filter((user: IApiUser) => user.IsInvestigator).map((user: DistanceFilterUser, index: number) => this.enrichUser(user, index));
    this.filteredUsers = [...this.allUsers];
    this.updateSelectedIdSet();
  }

  public onAddressSelected(address: any) {
    this.proximityAddress = address;
    if (address && address.latitude && address.longitude) {
      this.calculateDistances(address);
    } else {
      this.resetSort();
      this.selectedId = [];
      this.updateSelectedIdSet();
    }
    this.selectionChanged.emit(this.selectedId);
    this.emitSelectionWithMetrics();
    this.cdr.detectChanges();
  }

  private calculateDistances(address: any) {
    this.loading = true;
    const origin = `${address.latitude},${address.longitude}`;
    const batchSize = 25;
    const requests = [];

    // Filter users who have an address
    const usersWithAddress = this.allUsers.filter(u => u.HomeAddress?.latitude && u.HomeAddress?.longitude);
    const usersWithoutAddress = this.allUsers.filter(u => !u.HomeAddress?.latitude || !u.HomeAddress?.longitude);

    for (let i = 0; i < usersWithAddress.length; i += batchSize) {
      const batch = usersWithAddress.slice(i, i + batchSize);
      const destinations = batch.map(u => `${u.HomeAddress.latitude},${u.HomeAddress.longitude}`).join('|');

      requests.push(this._mapService.getDistance(origin, destinations).pipe(
        catchError(() => of([]))
      ));
    }

    if (requests.length === 0) {
      this.loading = false;
      this.filteredUsers = [...this.allUsers];
      return;
    }

    forkJoin(requests).subscribe((results: any[]) => {
      let allDistances: any[] = [];
      results.forEach(res => {
        if (Array.isArray(res)) {
          allDistances = allDistances.concat(res);
        } else {
          allDistances.push(res);
        }
      });

      const distanceById = new Map<string, number>();
      const durationById = new Map<string, number>();
      usersWithAddress.forEach((user, index) => {
        const distance = allDistances[index]?.distance !== undefined ? allDistances[index].distance : Infinity;
        const duration = allDistances[index]?.duration !== undefined ? allDistances[index].duration : undefined;
        distanceById.set(user.id, distance);
        durationById.set(user.id, duration);
      });

      usersWithoutAddress.forEach(user => {
        distanceById.set(user.id, Infinity);
        durationById.set(user.id, undefined);
      });

      this.allUsers = this.allUsers
        .map(user => ({
          ...user,
          distance: distanceById.get(user.id) ?? Infinity,
          duration: durationById.get(user.id)
        }))
        .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));

      this.applyTextFilter();
      this.emitSelectionWithMetrics();
      this.loading = false;
      setTimeout(() => {
        this.multiSelect.focus();
        this.multiSelect.toggle();
      });

    }, error => {
      console.error('Error calculating distances', error);
      this.loading = false;

    });
  }

  private resetSort() {
    this.allUsers = [...this.allUsers]
      .map(user => ({ ...user, distance: undefined }))
      .sort((a, b) => (a._originalIndex ?? 0) - (b._originalIndex ?? 0));

    this.applyTextFilter();

  }

  public onSearchChange(search: string) {
    this.searchString = search || '';
    this.applyTextFilter();
  }

  private applyTextFilter() {
    if (this.searchString && this.searchString.trim().length > 0) {
      const search = this.searchString.toLowerCase();
      this.filteredUsers = this.allUsers.filter(u => u._searchText?.includes(search));
    } else {
      this.filteredUsers = [...this.allUsers];
    }

  }

  public toggleUser(ids: any[]) {
    this.selectedId = ids || [];
    this.updateSelectedIdSet();
    this.selectionChanged.emit(this.selectedId);
    this.emitSelectionWithMetrics();
  }

  public clear(): void {
    this.proximityAddress = null;
    this.selectedId = [];
    this.updateSelectedIdSet();
    this.resetSort();
    this.selectionChanged.emit([]);
    this.emitSelectionWithMetrics();
    this.cdr.detectChanges();
  }

  public isSelected(userId: string): boolean {
    return this.selectedIdSet.has(userId);
  }

  private updateSelectedIdSet(): void {
    this.selectedIdSet = new Set((this.selectedId || []).map((id: any) => `${id}`));
  }

  private emitSelectionWithMetrics(): void {
    const selectedIds = Array.from(this.selectedIdSet);
    const selectedUsers = this.allUsers.filter(user => selectedIds.includes(`${user.id}`));

    const payload: DistanceSelectionPayload = {
      userIds: selectedIds,
      metrics: selectedUsers.map(user => ({
        id: `${user.id}`,
        distance: user.distance,
        duration: user.duration,
      }))
    };

    this.selectionWithMetricsChanged.emit(payload);
  }

  private enrichUser(user: DistanceFilterUser, index: number): DistanceFilterUser {
    const searchText = [
      user.itemFullName,
      user.firstName,
      user.lastName,
      user.HomeAddress?.city,
      user.HomeAddress?.state
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return {
      ...user,
      _searchText: searchText,
      _originalIndex: index
    };
  }
}
